import {
	createContext,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { UnsuccessfulFetchError } from 'src/clients/common/errors';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ProductKey } from '@/components/signup/utils/products';
import { getUfoSubstepForPolling } from '@/components/signup/utils/signup-ufo-utils';
import {
	ufoEventAsyncArchitecturePollingStatus,
	ufoEventConfirmationPageStatusPolling,
	ufoEventGraphQLGetProvisioningStatus,
	ufoEventGraphQLSubscriptionSetup,
	ufoEventProvisioningStatusCloudIdAvailability,
	ufoEventRedirectToProduct,
} from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { ProductActivationStatusResponse } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useCofsClient } from '@/libraries/signup/bxpkit-core/src/hooks/use-cofs-client';
import { getDataFoundationEvent } from '@/utils/data-foundations/data-foundation-types';
import { graphqlSubscriptionsQueries } from '@/utils/graphql/graphql-queries';
import { useGraphQlSubscription } from '@/utils/graphql/useGraphqlSubscription';

import {
	trackPerceivedProvisioningEvent,
	trackGetProvisioningStatusLatencyEvent,
} from '../utils/analytics';
import { createProductUrl } from '../utils/create-product-url';

import { useConfirmationPageType } from './use-confirmation-page-type';
import { useGupsStatus } from './use-gups';
import {
	CloudStatus,
	initCloudStatusContext,
	DEFAULT_TIMEOUT,
	GRAPHQL_FALLBACK_TIMEOUT,
	CreateCloudStatus,
	CloudStatusProviderProps,
} from './utils/types';
import {
	getCloudIdFromProductActivationStatuses,
	fireFrontendProvisioningCompletedEvent,
	fireUfoEventForPollingResult,
	getAsyncActivationStatus,
	FireUfoEventForPollingResultProps,
	checkAllPackagesStatus,
} from './utils/use-cloud-status-utils';

const { GRAPHQL_WEBHOOK_PROVISIONING_LATENCY, GRAPHQL_SUBSCRIPTION_ACK } = UfoCustomTimingKey;

export function useCloudStatus() {
	return useContext(CloudStatusContext);
}

const CloudStatusContext = createContext<CloudStatus>(initCloudStatusContext);

type GetCloudStatusType = () => Promise<boolean>;

const PRODUCT_ACTIVATION_STATUS_MAX_ATTEMPTS = 5;

const getResponseFromError = (error: unknown): Response | undefined => {
	if (typeof error !== 'object' || error === null) {
		return undefined;
	}
	return (error as { response?: Response }).response;
};

export const CloudStatusProvider = ({
	isProduction = false,
	children,
	onDemandUrl,
	cloudId: cloudIdProp,
	platform: platformProp,
	requestId,
	graphQlTimeout = GRAPHQL_FALLBACK_TIMEOUT,
	isDirectBuy,
}: PropsWithChildren<CloudStatusProviderProps>) => {
	/* -------------------------------------------------------------------------- */
	/*                                    hooks                                   */
	/* -------------------------------------------------------------------------- */
	const {
		products,
		dataRegion,
		admin,
		isFoundingAdmin,
		signupSource,
		orchestrationId,
		orgId,
		edition,
		provisioningRequestId = '',
		workflowId = '',
		signupId,
		primaryCollectionProduct,
		deferSiteNaming,
		platform: platformFromUrl,
	} = useSearchParameters();

	/*
	 * D1D6-128 follow-up: the parent template captures `platform` once via
	 * `useSearchParameters()` and passes it down as a prop. In the deferred
	 * flow that prop is captured BEFORE the gate's URL rewrite, so it sticks
	 * at "routing" (the default) for the lifetime of the provider, causing
	 * the architecture switch below to skip the CCP polling branch and
	 * `getCloudStatus()` to resolve `ready: false` forever.
	 *
	 * Mirror the pattern used for `provisioningRequestId`/`orchestrationId`:
	 * read straight from `useSearchParameters()` here so we pick up the
	 * post-handoff URL value, but fall back to the prop on first render
	 * before the URL has been rewritten.
	 */
	const platform =
		platformFromUrl === 'ccp' || platformFromUrl === 'hams' ? platformFromUrl : platformProp;
	const cofsClient = useCofsClient();
	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addTenantIdCallback } = useGupsStatus();
	const {
		profile: { cohorts },
	} = useUserContext();

	/* -------------------------------------------------------------------------- */
	/*                                create state                                */
	/* -------------------------------------------------------------------------- */
	const [createCloudStatus, setCreateCloudStatus] = useState<CreateCloudStatus>('pending');
	const [cloudId, setCloudId] = useState<string>(cloudIdProp);
	const [perceivedProvisioningStartTime, setPerceivedProvisioningStartTime] = useState<
		number | undefined
	>(undefined);
	const [getCloudStatus, setGetCloudStatus] = useState<GetCloudStatusType>(() => async () => false);
	const productKey = products[0] as ProductKey;

	const [enrolledInGraphql, setEnrolledInGraphql] = useState<boolean>(
		orchestrationId && productKey !== ProductKey.TEAMWORK_COLLECTION ? true : false,
	);
	const [graphqlProvisionComplete, setGraphqlProvisionComplete] = useState<boolean>(false);
	const provisionPollingTimes = useRef<number>(0);
	const [readyPollCloudStatus, setReadyPollCloudStatus] = useState<boolean>(false);
	const [triggerOnSuccess, setTriggerOnSuccess] = useState<boolean>(false);
	const [onSuccessDuration, setOnSuccessDuration] = useState<number>(0);
	const [fireOnRedirectAnalytics, setFireOnRedirectAnalytics] = useState<boolean>(false);
	const [productActivationStatusResponses, setProductActivationStatusResponses] = useState<
		ProductActivationStatusResponse[]
	>([]);

	/* -------------------------------------------------------------------------- */
	/*                                  constants                                 */
	/* -------------------------------------------------------------------------- */
	const asyncArchRecoverableTimeout = 60000;
	const bundleProductKeys =
		products.length > 0 ? products.map((product) => product as ProductKey).join(',') : '';
	// Defer Site Naming experiment (D1D6-47): when deferSiteNaming=true is set,
	// the confirmation page hook orchestrates getRecommendedSiteName +
	// activateProduct on the user's behalf, so onDemandUrl will be missing on
	// first render. Treat that specific transient state as valid so the
	// CloudStatusProvider does not immediately flip to an error. Once
	// onDemandUrl is populated by the deferred activation hook (D1D6-54), this
	// clause stops applying and the normal `(!!onDemandUrl && !!products.length)`
	// clause takes over — which means the products-length check is still
	// enforced symmetrically with the control path.
	const hasRequiredParameters =
		isDirectBuy || (!!onDemandUrl && !!products.length) || (deferSiteNaming && !onDemandUrl);
	const currentScreen = useConfirmationPageType(createCloudStatus);
	const graphqlProvisioningMS = useRef(0);
	const { data: subscriptionData, error: subscriptionError } = useGraphQlSubscription({
		reactComponent: 'CloudStatusProvider GraphQL',
		skip: !enrolledInGraphql,
		subscriptionQuery: graphqlSubscriptionsQueries.provisioningStatusQuery,
		orchestrationId,
		onErrorCb: () => {
			setEnrolledInGraphql(false);
		},
	});
	/* -------------------------------------------------------------------------- */
	/*                                  callBacks                                 */
	/* -------------------------------------------------------------------------- */
	const paidLandingJiraAiCgsKey =
		typeof window !== 'undefined'
			? (() => {
					const params = new URL(window.location.href).searchParams;
					const cgsKey = params.get('cgsKey') ?? undefined;
					return cgsKey;
				})()
			: undefined;

	const onSuccess = useCallback(
		(startTime: number) => {
			const endTime = Date.now();

			createAnalyticsEvent({
				eventType: 'operational',
				action: 'succeeded',
				actionSubject: 'provisioning-loading-screen-with-improved-polling',
				source: 'bxpkit-wac-confirmation-page',
				attributes: {
					totalElapsedTime: endTime - startTime,
					timeDiffFromPrev5secondPoll: (endTime - startTime) % 5000,
					timeDiffFromNext5secondPoll: 5000 - ((endTime - startTime) % 5000),
					workflowId,
					cgsKey: paidLandingJiraAiCgsKey,
				},
			}).fire('signup');

			createAnalyticsEvent({
				eventType: 'operational',
				eventName: 'createCloudSucceeded',
				startTime,
				endTime,
				elapsedTime: endTime - startTime,
				attributes: {
					workflowId,
					cgsKey: paidLandingJiraAiCgsKey,
				},
			}).fire('signup');
			setTriggerOnSuccess(true);
			setCreateCloudStatus('success');
		},
		[createAnalyticsEvent, setCreateCloudStatus, workflowId, paidLandingJiraAiCgsKey],
	);
	const onError = useCallback(
		(startTime: number, error: unknown) => {
			const endTime = Date.now();
			createAnalyticsEvent({
				event: 'errored',
				eventName: 'createCloudErrored',
				startTime,
				endTime,
				elapsedTime: endTime - startTime,
				error,
				attributes: {
					workflowId,
					cgsKey: paidLandingJiraAiCgsKey,
				},
			}).fire('signup');

			if (workflowId) {
				setCreateCloudStatus('error-unrecoverable');
				return;
			}

			setCreateCloudStatus('error');
		},
		[createAnalyticsEvent, workflowId, paidLandingJiraAiCgsKey],
	);
	const onTimeout = useCallback(
		(startTime: number) => {
			const endTime = Date.now();

			createAnalyticsEvent({
				event: 'timedOut',
				eventName: 'createCloudTimedOut',
				startTime,
				endTime,
				elapsedTime: endTime - startTime,
				attributes: {
					workflowId,
					cgsKey: paidLandingJiraAiCgsKey,
				},
			}).fire('signup');

			if (workflowId) {
				setCreateCloudStatus('timeout-recoverable');
				return;
			}

			setCreateCloudStatus('timeout');
		},
		[createAnalyticsEvent, workflowId, paidLandingJiraAiCgsKey],
	);
	const onAsyncArchRecoverableTimeout = useCallback(() => {
		if (createCloudStatus === 'pending') {
			setCreateCloudStatus('timeout-recoverable');
		}
	}, [createCloudStatus, setCreateCloudStatus]);
	const createRedirectUrl = useCallback(
		(isOnlyUrl = false) => {
			const productUrl = createProductUrl({
				isProduction,
				onDemandUrl,
				products,
				dataRegion,
				cloudId,
				admin,
				createAnalyticsEvent,
				templateChoice: 'unknown',
				signupSource,
				isFoundingAdmin,
				orgId,
				primaryCollectionProduct,
			});

			if (isOnlyUrl) {
				return productUrl;
			}

			createAnalyticsEvent({
				...getDataFoundationEvent('ProductNavigationInitialized'),
				actionSubjectId: 'newSite',
				attributes: {
					productUrl,
					productKey,
					edition,
				},
			}).fire();

			return productUrl;
		},
		[
			admin,
			cloudId,
			products,
			dataRegion,
			onDemandUrl,
			signupSource,
			isProduction,
			isFoundingAdmin,
			createAnalyticsEvent,
			orgId,
			edition,
			productKey,
		],
	);
	const fallbackGraphqlToStatusPolling = useCallback(() => {
		// SLO "bad event" for GraphQL webhook provisioning status
		ufoEventGraphQLGetProvisioningStatus.failure({
			error: new Error('GraphQL timeout'),
			customData: {
				reactComponent: 'CloudStatusProvider',
				analyticsEvent: 'timedOut',
				orchestrationId,
			},
		});
		// fallback to control status polling
		setEnrolledInGraphql(false);
		return;
	}, [orchestrationId]);
	const startPerceivedProvisioningTimer = useCallback(() => {
		// Start the timer if it hasn't been started yet
		if (!perceivedProvisioningStartTime) {
			const sessionStorageStartTime = sessionStorage.getItem('startTime');

			// if startTime stored in sessionStorage
			if (sessionStorageStartTime) {
				const startTime = Number(sessionStorageStartTime);
				const currentTime = Date.now() / 1000;

				// if sessionStorage startTime is stale (greater than 20mins), reset
				if (currentTime - startTime > 1200) {
					sessionStorage.setItem('startTime', String(currentTime));
					setPerceivedProvisioningStartTime(currentTime);
					return;
				}

				setPerceivedProvisioningStartTime(startTime);
				return;
			}

			const startTimeSeconds = Date.now() / 1000;

			// add start time to sessionStorage to persist on page refresh
			sessionStorage.setItem('startTime', String(startTimeSeconds));

			setPerceivedProvisioningStartTime(startTimeSeconds);
		}
	}, [perceivedProvisioningStartTime]);
	const pollCloudStatus = useCallback(
		(startTime = Date.now(), run = 0) => {
			const poll = () => {
				const endTime = Date.now();
				const elapsedTime = endTime - startTime;
				const perceivedStartTime = perceivedProvisioningStartTime
					? perceivedProvisioningStartTime * 1000
					: startTime;
				const elapsedPerceivedTime = endTime - perceivedStartTime;
				const pollingResultPayload: FireUfoEventForPollingResultProps = {
					pollingResult: 'success',
					elapsedTime,
					platform,
					productKey,
					edition,
					createAnalyticsEvent,
					currentScreen,
					workflowId,
				};
				if (elapsedTime > DEFAULT_TIMEOUT) {
					fireUfoEventForPollingResult({
						...pollingResultPayload,
						pollingResult: 'timeout',
						error: new Error('pollCloudStatus timeout'),
					});
					onTimeout(startTime);
					return;
				}

				if (elapsedPerceivedTime > asyncArchRecoverableTimeout && workflowId) {
					onAsyncArchRecoverableTimeout();
				}

				getCloudStatus()
					.then((ready) => {
						if (ready) {
							fireUfoEventForPollingResult(pollingResultPayload);
							onSuccess(startTime);
						} else {
							pollCloudStatus(startTime, run + 1);
						}
					})
					.catch((error) => {
						fireUfoEventForPollingResult({
							...pollingResultPayload,
							pollingResult: 'error',
							error: error,
						});
						onError(startTime, error);
					});
			};

			// initial run, check immediately to avoid rate limiting issues
			if (run === 0) {
				poll();
				return;
			}
			// we expect the majority of requests to be completed in the 15-30 second range, wait until 15 seconds to begin fast polling
			if (run === 1) {
				setTimeout(() => poll(), 15000);
				return;
			}
			// in the 15-30 seconds range, fast poll every 2 seconds
			if (run > 1 && run < 10) {
				setTimeout(() => poll(), 2000);
				return;
			}
			// if provisioning is delayed, slow poll every 10 seconds until the timeout if necessary
			setTimeout(() => poll(), 10000);
		},
		[
			getCloudStatus,
			onError,
			onSuccess,
			onTimeout,
			onAsyncArchRecoverableTimeout,
			perceivedProvisioningStartTime,
			workflowId,
		],
	);

	/* -------------------------------------------------------------------------- */
	/*                                    calls                                   */
	/* -------------------------------------------------------------------------- */
	ufoEventProvisioningStatusCloudIdAvailability.start();

	/* -------------------------------------------------------------------------- */
	/*                                 useEffects                                 */
	/* -------------------------------------------------------------------------- */
	useEffect(() => {
		addTenantIdCallback(cloudId);
	}, [addTenantIdCallback, cloudId]);

	// GraphQL to listen to status change
	useEffect(() => {
		// if graphql is not enrolled or already logged provisioning complete
		if (!enrolledInGraphql || graphqlProvisionComplete) {
			return;
		}
		try {
			if (!!subscriptionData) {
				// SLO "good event" for GraphQL subscription
				ufoEventGraphQLSubscriptionSetup.success({
					customData: {
						reactComponent: 'CloudStatusProvider GraphQL',
						analyticsEvent: 'succeeded',
						orchestrationId,
					},
				});
				ufoEventRedirectToProduct.endSubstep(GRAPHQL_SUBSCRIPTION_ACK);
			}
			// Guard to redirect users who bookmarked Confirmation page
			ufoEventGraphQLGetProvisioningStatus.start({
				productKey,
				bundleProductKeys,
			});
			if (provisionPollingTimes.current === 0) {
				const before = Date.now();
				getCloudStatus()
					.then((ready) => {
						ufoEventGraphQLGetProvisioningStatus.setCustomData({
							isInitialStatusPollingStatusOk: true,
						});
						if (ready) {
							setGraphqlProvisionComplete(true);
							setTriggerOnSuccess(true);
							setOnSuccessDuration(Date.now() - before);
						}
					})
					.catch(() => {
						// Do not throw on initial polling failure. Wait for GraphQL response
						ufoEventGraphQLGetProvisioningStatus.setCustomData({
							isInitialStatusPollingStatusOk: false,
						});
					});
			}
			ufoEventRedirectToProduct.startSubstep(GRAPHQL_WEBHOOK_PROVISIONING_LATENCY);
			if (subscriptionData?.signup?.signupProvisioningStatus) {
				const {
					provisionComplete,
					orchestrationId: orchestrationIdFromSubscription,
					cloudId,
				} = subscriptionData.signup.signupProvisioningStatus;
				if (cloudId) {
					setCloudId(cloudId);
				}
				if (provisionComplete && orchestrationIdFromSubscription) {
					trackGetProvisioningStatusLatencyEvent({
						createAnalyticsEvent,
						productKey,
						provisioningScreenOnComplete: currentScreen,
						fetchType: 'graphql-webhook',
						elapsedTimeSeconds: graphqlProvisioningMS.current / 1000,
						workflowId,
					});

					// SLO "good event" for GraphQL get provisioning status
					ufoEventGraphQLGetProvisioningStatus.success({
						customData: {
							reactComponent: 'CloudStatusProvider GraphQL',
							analyticsEvent: 'succeeded',
							elapsedTimeMs: graphqlProvisioningMS.current,
							orchestrationId: orchestrationIdFromSubscription,
						},
					});
					fireFrontendProvisioningCompletedEvent(
						'graphql',
						productKey,
						edition,
						createAnalyticsEvent,
						workflowId,
					);

					setGraphqlProvisionComplete(true);
					setTriggerOnSuccess(true);
					setOnSuccessDuration(graphqlProvisioningMS.current);
				}
			}
			if (subscriptionError) {
				// SLO "bad event" for GraphQL subscription
				ufoEventGraphQLSubscriptionSetup.failure({
					error: subscriptionError,
					customData: {
						reactComponent: 'CloudStatusProvider GraphQL',
						analyticsEvent: 'errored',
						errorOrigin: 'useSubscription, useEffect',
						orchestrationId,
					},
				});
				throw subscriptionError;
			}
		} catch (error) {
			// SLO "bad event" for GraphQL get provisioning status
			ufoEventGraphQLGetProvisioningStatus.failure({
				error,
				customData: {
					reactComponent: 'CloudStatusProvider GraphQL',
					analyticsEvent: 'errored',
					elapsedTimeMs: graphqlProvisioningMS.current,
					errorOrigin: 'useEffect',
					orchestrationId,
				},
			});
			ufoEventRedirectToProduct.endSubstep(GRAPHQL_WEBHOOK_PROVISIONING_LATENCY, {
				customData: { orchestrationId },
			});

			// fallback to control status polling
			setEnrolledInGraphql(false);
		}
	}, [
		enrolledInGraphql,
		graphqlProvisionComplete,
		subscriptionData,
		subscriptionError,
		getCloudStatus,
	]);

	// check if graphQl has timed out, if so disable graphQl subscription
	useEffect(() => {
		if (!enrolledInGraphql || graphqlProvisionComplete) {
			return;
		}

		const timeoutId = setTimeout(() => {
			fallbackGraphqlToStatusPolling();
		}, graphQlTimeout);

		return () => clearTimeout(timeoutId);
	}, [enrolledInGraphql, graphqlProvisionComplete, graphQlTimeout, fallbackGraphqlToStatusPolling]);

	// set getCloudStatus values based on CCP VS HAMS VS ASYNC ARCHITECTURE
	useEffect(() => {
		// ASYNC ARCHITECTURE
		if (workflowId) {
			setGetCloudStatus(() => () => {
				return getAsyncActivationStatus(stargateClient, workflowId);
			});
		}
		// CCP
		else if (platform === 'ccp' || isDirectBuy || productKey === ProductKey.LOOM) {
			ufoEventRedirectToProduct.setCustomData({
				provisioningRequestId,
			});
			setGetCloudStatus(() => async () => {
				if (!provisioningRequestId) {
					return false;
				}
				provisionPollingTimes.current = provisionPollingTimes.current + 1;

				const requestIdsResp = async (product: string) =>
					await stargateClient.getProductActivationStatus({
						provisioningRequestId: product,
						provisionPollingTimes: provisionPollingTimes.current,
					});

				const requestIdsRespWithRetry = async (product: string) => {
					for (let attempt = 1; attempt <= PRODUCT_ACTIVATION_STATUS_MAX_ATTEMPTS; attempt++) {
						try {
							return await requestIdsResp(product);
						} catch (error) {
							const response = getResponseFromError(error);

							// Redirect user to product if 400 on bookmarked users
							if (response?.status === 400) {
								ufoEventRedirectToProduct.abort({
									customData: {
										reactComponent: 'CloudStatusProvider',
										analyticsEvent: 'redirect',
										redirectFrom: 'confirmationPage',
										errorOrigin: 'StaleProvisioningRedirect',
									},
									error,
								});
								window.location.assign(createRedirectUrl(true));
								throw error;
							}

							if (attempt >= PRODUCT_ACTIVATION_STATUS_MAX_ATTEMPTS) {
								throw error;
							}
						}
					}

					throw new Error('Product activation status retry exhausted');
				};

				const requestIds = await Promise.all(
					provisioningRequestId.split(',').map(async (product) => {
						return requestIdsRespWithRetry(product);
					}),
				);

				setProductActivationStatusResponses(requestIds);

				// check for cloudId in the status response
				if (!cloudId) {
					const detectedCloudId = getCloudIdFromProductActivationStatuses(requestIds);
					if (detectedCloudId) {
						setCloudId(detectedCloudId);
					}
				}

				const checkAllPackagesStatuses = checkAllPackagesStatus(requestIds, cloudId);

				// biz use case needs to make sure rovo is finished provisioning before proceeding.
				if (cohorts?.domainType === 'business-domain' && cloudId) {
					try {
						const { products } = await stargateClient.getLicenceInformationStatus({
							cloudId,
						});
						const edition = products['confluence.ondemand']?.edition;
						if (edition === 'premium') {
							const rovoData = products['rovo.ondemand'];
							if (rovoData?.state !== 'ACTIVE') {
								return Promise.resolve(false);
							}
						}
					} catch (error) {
						const status =
							error instanceof UnsuccessfulFetchError ? error.response.status : undefined;
						createAnalyticsEvent({
							action: 'failed',
							actionSubject: 'getLicenceInformationStatus',
							eventType: 'track',
							attributes: {
								cloudId,
								productKey,
								platform,
								currentScreen,
								provisioningRequestId,
								status,
							},
						}).fire();
						return checkAllPackagesStatuses;
					}
				}
				return checkAllPackagesStatuses;
			});
		}
		// HAMS
		else {
			setGetCloudStatus(() => async () => {
				if (!cloudId || !requestId) {
					return false;
				}
				provisionPollingTimes.current = provisionPollingTimes.current + 1;
				const cloudStatusResp = async () => {
					const { completed, successful } = await cofsClient.getCloudStatus({
						cloudId,
						requestId,
					});
					return completed && successful;
				};

				try {
					return await cloudStatusResp();
				} catch {
					// retry if the first call fails
					return await cloudStatusResp();
				}
			});
		}
		ufoEventRedirectToProduct.setCustomData({
			cloudId,
		});
	}, [platform, provisioningRequestId, primaryCollectionProduct, requestId]);

	// sets READY TO POLL if not graphQl and has required params
	useEffect(() => {
		if (enrolledInGraphql) {
			return;
		}
		const { timingKey, requestPath } = getUfoSubstepForPolling(platform);

		// Add product cohort to UFO event (ie. metrics by product)
		// Start UFO to measure the total duration and reliability of provisioning status polling
		// conditionally define ufo event based on if async architecture
		const ufoEventStatusPolling = workflowId
			? ufoEventAsyncArchitecturePollingStatus
			: ufoEventConfirmationPageStatusPolling;

		// Defer Site Naming experiment (D1D6-47): when deferSiteNaming=true is
		// set, the deferred activation hook is responsible for calling
		// getRecommendedSiteName + activateProduct and then rewriting the URL
		// with the resulting onDemandUrl / provisioningRequestId. Until that
		// happens, polling consumers (e.g. use-cloud-status-direct-buy-redirection)
		// must not start polling — there is nothing to poll yet. We also
		// intentionally skip the UFO `start`/`startSubstep` calls below so we
		// don't leak a started-but-never-completed event on the first render
		// and then double-start it on the re-render once onDemandUrl arrives.
		if (deferSiteNaming && !onDemandUrl) {
			return;
		}

		ufoEventStatusPolling.start({
			productKey,
			bundleProductKeys,
		});
		ufoEventRedirectToProduct.startSubstep(timingKey);

		if (!hasRequiredParameters) {
			if (createCloudStatus !== 'error') {
				// SLO "bad event" for provisioning status polling
				ufoEventStatusPolling.failure({
					error: new Error('missingRequiredParameters'),
					customData: {
						reactComponent: 'CloudStatusProvider',
						analyticsEvent: 'missingRequiredParameters',
						provisioningStatus: createCloudStatus,
						errorOrigin: 'hasRequiredParameters',
					},
				});

				// Performance metrics for polling duration
				ufoEventRedirectToProduct.endSubstep(timingKey, {
					error: new Error('missingRequiredParameters'),
					customData: {
						provisioningStatus: createCloudStatus,
						reqPath: requestPath,
						errorOrigin: 'hasRequiredParameters',
					},
				});

				createAnalyticsEvent({
					event: 'errored',
					eventName: 'missingRequiredParameters',
				}).fire('signup');
				setCreateCloudStatus('error');
			}
			return;
		}

		setReadyPollCloudStatus(true);
	}, [hasRequiredParameters, getCloudStatus, enrolledInGraphql, deferSiteNaming, onDemandUrl]);

	// On provisioning finish UFO, analytics and redirection handling
	useEffect(() => {
		if (enrolledInGraphql && !graphqlProvisionComplete) {
			return;
		}
		if (createCloudStatus !== 'success') {
			return;
		}

		const fireProvisionSuccessUFOAnalyticsEvent = () => {
			createAnalyticsEvent({
				eventName: 'redirectedToProduct',
				eventComponent: 'confirmationPage',
				event: 'redirected',
				eventType: 'ui',
				action: 'redirected',
			}).fire();

			trackPerceivedProvisioningEvent({
				createAnalyticsEvent,
				productKey,
				finalProvisioningScreen: currentScreen,
				startTimeSeconds: perceivedProvisioningStartTime,
				signupId,
			});

			// SLO "good event" for redirect into product
			if (graphqlProvisionComplete) {
				ufoEventRedirectToProduct.success({
					customData: {
						reactComponent: 'CloudStatusProvider GraphQL',
						analyticsEvent: 'redirected',
						redirectFrom: 'GraphQLWebhook',
						reqPath: 'graphql-subscription',
						orchestrationId,
					},
				});
			} else {
				ufoEventRedirectToProduct.success({
					customData: {
						reactComponent: `CloudStatusProvider ${currentScreen}`,
						analyticsEvent: 'redirected',
						redirectFrom: 'confirmationPage',
						provisioningStatus: 'success',
						finalProvisioningScreen: currentScreen,
					},
				});
			}
		};

		if (fireOnRedirectAnalytics) {
			fireProvisionSuccessUFOAnalyticsEvent();
		}
	}, [currentScreen, createCloudStatus, graphqlProvisionComplete, fireOnRedirectAnalytics]);

	return (
		<CloudStatusContext.Provider
			value={{
				createCloudStatus,
				readyPollCloudStatus,
				isProduction,
				onDemandUrl,
				cloudId,
				platform,
				requestId,
				triggerOnSuccess,
				onSuccessDuration,
				perceivedProvisioningStartTime,
				productActivationStatusResponses,
				createRedirectUrl,
				onSuccess,
				startPerceivedProvisioningTimer,
				pollCloudStatus,
				setFireOnRedirectAnalytics,
				setCreateCloudStatus,
				getCloudStatus,
			}}
		>
			{children}
		</CloudStatusContext.Provider>
	);
};
